import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Article: "Traceability from charge to casting, and why it changes recalls".
 *
 * Author's intended goal: "Quality" (the digital-thread / genealogy idea).
 * The published `ArticleGoal` union is currently Educational | ROI | Technical,
 * so this is filed under "Technical" — it explains the mechanism of the digital
 * thread. If a "Quality" goal is added to the union later, switch `goal` below.
 *
 * Honest, vendor-neutral body; the product appears only in the closing block.
 * Every figure is illustrative and reads as a generic range.
 */
export const TRACEABILITY_CHARGE_TO_CASTING_ARTICLE: Article = {
  slug: "traceability-from-charge-to-casting",
  title: "Traceability from charge to casting, and why it changes recalls",
  deck:
    "A casting is the end of a long chain: charge, melt, sand, mould, pour, specimen. If every link carries an identity, a quality problem stops being a guessing game. You can walk backward to the cause, or forward to every part it touched.",
  goal: "Technical",
  readingMins: 8,
  heroStat: { value: "1 ID", label: "per output — the thread that ties the whole chain together" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "When a customer returns a batch of castings with a crack that should not be there, the clock starts. The question is brutally simple and usually impossible to answer quickly: what else is affected? Which heats, which moulds, which shipments share whatever went wrong? In most foundries the honest answer is a shrug followed by a conservative quarantine — scrap a week, hold three shipments, and hope you cast a wide enough net. Traceability is what turns that shrug into a precise, defensible list. It is not paperwork for its own sake. It is the difference between a surgical recall and a panicked one.",
    },
    {
      kind: "paragraph",
      text:
        "The idea underneath it is older than software and is sometimes called a digital thread: every output of every step carries an identity, and every identity records what it was made from and the conditions under which it was made. Do that consistently down the line and you get a chain you can read in either direction. Backward, it answers 'what caused this?'. Forward, it answers 'what else did this affect?'. Those are the only two questions a recall ever asks.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The chain",
      heading: "Every step produces something that deserves a name",
      sub: "A casting is not one record — it is the last link in a chain of identified lots.",
    },
    {
      kind: "processFlow",
      title: "The genealogy of one casting",
      steps: [
        {
          title: "Charge",
          detail: "Each charge make-up — returns, pig, scrap, alloy additions — gets a lot ID and a recorded composition.",
          icon: "boxes",
        },
        {
          title: "Heat",
          detail: "Tapping groups charges into a heat with its own ID, tap temperature and spectrometer result.",
          icon: "factory",
        },
        {
          title: "Sand & mould",
          detail: "The sand batch (moisture, compactability) and the mould it produced each carry IDs that link to the line.",
          icon: "workflow",
        },
        {
          title: "Pour",
          detail: "Pouring binds a heat ID to a mould ID at a recorded temperature and time — the moment two threads join.",
          icon: "gitBranch",
        },
        {
          title: "Casting",
          detail: "The cooled casting inherits both parents and gets its own serial — the leaf of the family tree.",
          icon: "search",
        },
        {
          title: "Specimen",
          detail: "Test bars and inspection results attach to the casting and heat, closing the loop on quality.",
          icon: "shieldCheck",
        },
      ],
      caption:
        "Each arrow is a parent-child link. Because the links are explicit, the chain reads both ways — and that is the whole trick.",
    },
    {
      kind: "paragraph",
      text:
        "Notice what the chain is made of: not a single flat row in a logbook, but a series of lots that split and merge. One heat is poured into many moulds, so a heat ID fans out to many castings. One sand batch feeds a run of moulds. A charge is many ingredients merged into one melt. Modelling those split and merge operations honestly is what lets you follow material the way it actually flowed, rather than pretending each casting came from a tidy, isolated recipe.",
    },
    {
      kind: "pullQuote",
      quote:
        "A recall asks only two questions — what caused this, and what else did it touch. Traceability is simply the discipline of being able to answer both before the customer calls again.",
      attribution: "The logic of a defensible recall",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Backward",
      heading: "Root cause: walk the thread up from the defect",
    },
    {
      kind: "paragraph",
      text:
        "Start at a cracked casting and follow its parents. The serial points to a pour event; the pour points to a heat and a mould; the heat carries its tap temperature and chemistry; the mould carries the sand batch behind it. In a few hops you are standing at the conditions that produced the part — and you can compare them against the good castings from the same week. If the bad parts cluster on one heat with a low pour temperature, or one sand batch that ran wet, the cause stops being a theory. The thread took you there.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Forward",
      heading: "Containment: walk the thread down to every affected part",
    },
    {
      kind: "paragraph",
      text:
        "Now run it the other way. Once you suspect a specific heat or sand batch, follow its children: every mould it poured, every casting that inherited it, every shipment those castings went into. The output is a precise list of serials and customers — not 'probably last Tuesday'. That precision is the entire economic argument for traceability, because the alternative is to quarantine everything that might be implicated, and 'might' is always far larger than 'is'.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: castings quarantined in a recall, without vs. with traceability",
      unit: "castings held",
      bars: [
        { label: "Without traceability — hold everything plausibly affected", value: 4200, tone: "danger", display: "~4,200" },
        { label: "With a digital thread — hold only the linked serials", value: 380, tone: "success", display: "~380" },
      ],
      caption:
        "Generic, illustrative figures. The ratio is the point: when you cannot prove what is clean, you scrap the doubt — and the doubt is always the bigger pile.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "The paper-trail trap",
      text:
        "Traceability kept as binders and spreadsheets technically exists, but it cannot be queried under time pressure. If answering 'which castings came from heat 4471?' takes two people half a day, you will quarantine broadly instead — which means the records did not actually save you anything.",
    },
    {
      kind: "chart",
      chart: "bar",
      title: "Illustrative: hours to scope a recall as the chain gets more connected",
      xKey: "approach",
      unit: "h",
      series: [{ dataKey: "hours", label: "Hours to a defensible list", tone: "warning" }],
      data: [
        { approach: "Paper logs", hours: 36 },
        { approach: "Spreadsheets", hours: 14 },
        { approach: "Linked records", hours: 3 },
        { approach: "Live genealogy", hours: 1 },
      ],
      caption:
        "Generic, illustrative figures. As the links become machine-readable, scoping a recall collapses from days to a query.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Why it pays",
      heading: "Traceability is insurance you also use every day",
    },
    {
      kind: "paragraph",
      text:
        "The recall case is the dramatic one, but the same thread earns its keep quietly all year. The data that lets you scope a recall is exactly the data that lets you attribute scrap to a cause, prove conformance to a customer audit, and answer 'what was different about the bad batch?' without a meeting. You build the chain once; it pays out in containment, in root cause, and in the simple confidence of being able to say precisely where any casting came from.",
    },
    {
      kind: "statCallout",
      value: "days → minutes",
      label: "the shift in how long it takes to scope a quality event",
      caption:
        "Illustrative, not a guarantee. The change is qualitative: a question that used to require archaeology becomes a query you run while the customer is still on the phone.",
      tone: "success",
    },
    {
      kind: "paragraph",
      text:
        "None of this requires barcoding every grain of sand. It requires deciding, at each step, what the output is and giving it an identity that records its parents and its conditions. Do that down the chain and a casting is no longer an anonymous lump of iron. It is a part with a history — and a history is the only thing that ever answers a recall.",
    },
    {
      kind: "sahasEnables",
      intro:
        "The digital-thread idea above is vendor-neutral — any disciplined shop can pursue it. What the platform adds is a data model where genealogy is automatic, so the chain is always there when you need to walk it.",
      capabilities: [
        {
          title: "Genealogy by construction",
          detail:
            "A material-lot model captures split, merge and transform at every step, so each casting already knows its charge, heat, sand and mould without manual linking.",
          icon: "gitBranch",
        },
        {
          title: "Walk it either direction",
          detail:
            "Trace any casting backward to its root cause, or any heat forward to every part and shipment it touched — both as a single query.",
          icon: "search",
        },
        {
          title: "Scope a recall in minutes",
          detail:
            "When a quality event hits, the affected serials are a filter away — turning a broad, defensive quarantine into a precise, defensible list.",
          icon: "shieldCheck",
        },
      ],
    },
  ],
};
